import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '~/hooks';
import { Language, languages, LanguageT } from '~/lib/types';
import { cn } from '~/lib/utils';
import { Button } from '../ui/button';

export default function LanguageSelector() {
  const [open, setOpen] = React.useState(false);
  const [language, setLanguage] = React.useState<LanguageT>(Language.ES);
  const { i18n } = useTranslation();

  React.useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || Language.ES;
    i18n.changeLanguage(savedLanguage);
    setLanguage(savedLanguage as LanguageT);
  }, []);

  const handleLanguageChange = (lang: LanguageT) => {
    localStorage.setItem('language', lang);
    i18n.changeLanguage(lang);
    setLanguage(lang);
    setOpen(false);
  };

  const currentLang = languages.find((l) => l.code === language) || languages[0];

  const menuRef = useClickOutside<HTMLDivElement>(() => setOpen(false));

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        onClick={() => setOpen(!open)}
        className="flex w-20 items-center space-x-2 rounded-md border px-3 py-2 bg-background hover:bg-accent transition-all"
      >
        <img src={getFlag(currentLang.code)} alt={currentLang.label} className="w-6 h-6 rounded-full" />
        <span>{currentLang.short}</span>
      </Button>
      <div
        className={cn(
          'space-y-1.5 p-2 absolute right-0 mt-2 w-32 rounded-lg border bg-background shadow-lg overflow-hidden transform transition-all duration-300 ease-in-out',
          open ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        )}
      >
        {languages.map((lang) => (
          <Button
            variant="ghost"
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={cn(
              'p-1 px-2 flex w-full items-center justify-start space-x-2  py-3 text-left hover:bg-gray-200 dark:hover:bg-gray-700 transition-all',
              lang.code === language && 'bg-gray-300 dark:bg-gray-800 font-bold'
            )}
          >
            <img src={getFlag(lang.code)} alt={lang.label} className="w-6 h-6 rounded-full" />
            <span>{lang.label}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}

const getFlag = (code: LanguageT) => `/assets/${code}-flag.svg`;
